/* eslint-disable operator-assignment */
/* eslint-disable no-bitwise */
import { invariant } from '../util/invariant';

export class BitWriter {

  bytes: number[] = [];
  private currentByte = 0;
  private availableBitsInCurrentByte = 8;

  /**
   * Note that this function will only work with nBits in the [0;31] range,
   * unlike writeBits() that accepts nBits == 32
   */
  writeSignedBits(value: number, nBits: number) {
    value = value & ((1 << nBits) - 1);
    this.writeBits(value, nBits);
  }

  writeBits(value: number, nBits: number) {
    invariant(value >= 0 && value <= 0xFFFFFFFF, `Invalid value: ${value}`);
    invariant(nBits > 0 && nBits <= 32, `Invalid nBits: ${nBits}`);

    let bitsWritten = 0;
    while (bitsWritten != nBits) {
      const remainingBits = nBits - bitsWritten;
      const bitsToWrite = Math.min(this.availableBitsInCurrentByte, remainingBits);

      const nBitsToShiftRight = (nBits - bitsWritten - bitsToWrite);
      const shiftedValue = (value >> nBitsToShiftRight) & 0xFF;

      const mask = (1 << bitsToWrite) - 1;
      const maskedValue = shiftedValue & mask;

      const availableBitsInByteAfterThis = this.availableBitsInCurrentByte - bitsToWrite;
      const shiftedMaskedValue = maskedValue << availableBitsInByteAfterThis;

      this.currentByte = this.currentByte | shiftedMaskedValue;

      this.availableBitsInCurrentByte = availableBitsInByteAfterThis;

      if (this.availableBitsInCurrentByte === 0) {
        this.bytes.push(this.currentByte);
        this.currentByte = 0;
        this.availableBitsInCurrentByte = 8;
      }

      bitsWritten += bitsToWrite;
    }
  }


  writeUTF8(n: number) {
    invariant(n >= 0, `Invalid value: ${n}`);
    if (n <= 0x007F) {         // 0xxxxxxx
      this.writeBits(n, 8);
    } else if (n <= 0x07FF) {  // 110xxxxx 10xxxxxx
      this.writeBits(0b110, 3);
      this.writeBits((n >> 6) & 0xFF, 5);

      this.writeBits(0b10, 2);
      this.writeBits(n & 0b111111, 6);
    } else if (n <= 0xFFFF) {  // 1110xxxx 10xxxxxx 10xxxxxx
      this.writeBits(0b1110, 4);
      this.writeBits((n >> 12) & 0xFF, 4);

      this.writeBits(0b10, 2);
      this.writeBits((n >> 6) & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits(n & 0b111111, 6);
    } else if (n <= 0x1FFFFF) {  // 11110xxx 10xxxxxx 10xxxxxx 10xxxxxx
      this.writeBits(0b11110, 5);
      this.writeBits((n >> 18) & 0xFF, 3);

      this.writeBits(0b10, 2);
      this.writeBits((n >> 12) & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits((n >> 6) & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits(n & 0b111111, 6);
    } else if (n <= 0x3FFFFFF) {  // 111110xx 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx
      this.writeBits(0b111110, 6);
      this.writeBits((n >> 24) & 0xFF, 2);

      this.writeBits(0b10, 2);
      this.writeBits((n >> 18) & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits((n >> 12) & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits((n >> 6) & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits(n & 0b111111, 6);
    } else if (n <= 0x7FFFFFFF) {  // 1111110x 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx
      this.writeBits(0b1111110, 7);
      this.writeBits((n >> 30) & 0xFF, 1);

      this.writeBits(0b10, 2);
      this.writeBits((n >> 24) & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits((n >> 18) & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits((n >> 12) & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits((n >> 6) & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits(n & 0b111111, 6);
    } else if (n <= 0xFFFFFFFFF) {  // 11111110 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx 10xxxxxx
      this.writeBits(0b11111110, 8);

      this.writeBits(0b10, 2);

      // shifts on values larger than 2 bits are truncated so we need
      // special handling here
      const x = Math.floor(n / (1 << 30));
      this.writeBits(x & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits((n >> 24) & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits((n >> 18) & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits((n >> 12) & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits((n >> 6) & 0b111111, 6);

      this.writeBits(0b10, 2);
      this.writeBits(n & 0b111111, 6);
    }
  }

  zeroPadLastByteIfNeeded() {
    if (this.availableBitsInCurrentByte !== 8) {
      this.bytes.push(this.currentByte);
      this.currentByte = 0;
      this.availableBitsInCurrentByte = 8;
    }
  }

  getBuffer(): Uint8Array {
    invariant(this.availableBitsInCurrentByte === 8, 'Incomplete last byte');
    return new Uint8Array(this.bytes);
  }

  writeString(s: string) {
    for (let i = 0; i < s.length; i++) {
      this.writeUTF8(s.charCodeAt(i));
    }
  }

  write16LE(n: number) {
    const a = (n >> 8) & 0xFF;
    const b = n & 0xFF;
    this.writeBits(b, 8);
    this.writeBits(a, 8);
  }

}
